import {
  ApiActiveAnnouncementResponse,
  ApiAnnouncement,
  ApiAnnouncementsResponse,
  ApiCreateAnnouncementRequest,
  ApiUpdateAnnouncementRequest,
} from '@repo/types';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useQueryKeys } from '@src/hooks/useQueryKeys';
import { useApi } from './useApi';

export function useGetActiveAnnouncement(enabled: boolean = true) {
  const api = useApi();
  const queryKey = useQueryKeys(['active-announcement']);

  return useQuery(
    queryKey,
    async () => {
      const response = await api.get('/announcements/active');
      const data = (await response?.json()) as ApiActiveAnnouncementResponse;
      return data.announcement;
    },
    { enabled }
  );
}

export function useGetAnnouncements(enabled: boolean = true) {
  const api = useApi();
  const queryKey = useQueryKeys(['announcements']);

  return useQuery(
    queryKey,
    async () => {
      const response = await api.get('/announcements');
      const data = (await response?.json()) as ApiAnnouncementsResponse;
      return data.announcements;
    },
    { enabled }
  );
}

function useInvalidateAnnouncements() {
  const queryClient = useQueryClient();
  const announcementsKey = useQueryKeys(['announcements']);
  const activeKey = useQueryKeys(['active-announcement']);

  return () => {
    queryClient.invalidateQueries({ queryKey: announcementsKey });
    queryClient.invalidateQueries({ queryKey: activeKey });
  };
}

export function useCreateAnnouncement() {
  const api = useApi();
  const invalidate = useInvalidateAnnouncements();

  return useMutation({
    mutationFn: async (data: ApiCreateAnnouncementRequest) => {
      const response = await api.post('/announcements', data);
      return (await response?.json()) as ApiAnnouncement;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateAnnouncement() {
  const api = useApi();
  const invalidate = useInvalidateAnnouncements();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: ApiUpdateAnnouncementRequest;
    }) => {
      const response = await api.patch(`/announcements/${id}`, data);
      return (await response?.json()) as ApiAnnouncement;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteAnnouncement() {
  const api = useApi();
  const invalidate = useInvalidateAnnouncements();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.del(`/announcements/${id}`);
    },
    onSuccess: invalidate,
  });
}
